import type {
	GetAccommodationResponse,
	GetEventResponse,
	GetExpenseResponse,
	GetFlightGroupResponse,
	GetIdeaResponse,
	GetPayUpType,
	GetUserType,
} from '@orbit/api-types'
import type { ChatTagPickerItem } from './tagPicker'
import { makeCard, memberCard, type ChatAuthor, type ChatCard } from './types'

/*
 * What can be tagged in a trip, built from the trip data the webapp already holds. Each
 * card is made the way the server makes it (`chat-tag.service.ts`), so an attached card
 * reads the same once it's sent. Ported from iOS `ChatTagSource`.
 */

type Member = Pick<GetUserType, 'id' | 'firstName' | 'lastName' | 'profilePicture'>

export const authorOf = ({ id, firstName, lastName, profilePicture }: Member): ChatAuthor => ({
	id,
	firstName,
	lastName,
	profilePicture,
})

/** The members whose ids are given, in id order as the server lists them, once each. */
const peopleOf = (ids: number[], members: readonly Member[]): ChatAuthor[] => {
	const byId = new Map(members.map(member => [member.id, member]))
	return [...new Set(ids)].flatMap(id => {
		const member = byId.get(id)
		return member ? [authorOf(member)] : []
	})
}

/** Like the server's: "LAX → HND" from the first departure to the last arrival, or the first flight's number. */
const routeOf = (legs: GetFlightGroupResponse['flights']): string => {
	const first = legs[0]
	const last = legs[legs.length - 1]
	if (!first || !last) return 'Flight'
	if (first.departureAirportCode && last.arrivalAirportCode)
		return `${first.departureAirportCode} → ${last.arrivalAirportCode}`
	return [first.airlineIata, first.flightNumber].filter(Boolean).join(' ')
}

export const flightGroupCard = (group: GetFlightGroupResponse, members: readonly Member[]): ChatCard => {
	const legs = [...group.flights].sort((a, b) => a.legIndex - b.legIndex)
	const passengers = legs.flatMap(leg => leg.passengers.map(p => p.userId).sort((a, b) => a - b))
	return makeCard('FlightGroup', group.id, routeOf(legs), {
		people: peopleOf(passengers, members),
		legs: legs.map(leg => ({
			airlineIata: leg.airlineIata,
			flightNumber: leg.flightNumber,
			departureAirportCode: leg.departureAirportCode,
			arrivalAirportCode: leg.arrivalAirportCode,
			departureTimeStr: leg.departureTimeStr,
			arrivalTimeStr: leg.arrivalTimeStr,
		})),
	})
}

export const accommodationCard = (accommodation: GetAccommodationResponse, members: readonly Member[]): ChatCard =>
	makeCard('Accommodation', accommodation.id, accommodation.name, {
		people: peopleOf(
			accommodation.guests.map(guest => guest.userId).sort((a, b) => a - b),
			members,
		),
		checkInDate: accommodation.checkInDate,
		checkOutDate: accommodation.checkOutDate,
	})

export const eventCard = (event: GetEventResponse): ChatCard =>
	makeCard('TripEvent', event.id, event.name, {
		tripDayIndex: event.tripDayIndex,
		startTime: event.startTime,
		category: event.category,
		thumbnail: event.thumbnail,
	})

export const ideaCard = (idea: GetIdeaResponse): ChatCard =>
	makeCard('Idea', idea.id, idea.name, { category: idea.category, thumbnail: idea.thumbnail })

export const expenseCard = (expense: GetExpenseResponse): ChatCard =>
	makeCard('Expense', expense.id, expense.name, {
		date: expense.expenseDate,
		amount: expense.amount,
		currencyCode: expense.currencyCode,
	})

export const payUpCard = (payUp: GetPayUpType, members: readonly Member[]): ChatCard => {
	const name = (id: number, fallback: string) => members.find(m => m.id === id)?.firstName ?? fallback
	return makeCard('PayUp', payUp.id, `${name(payUp.fromUserId, 'Someone')} paid ${name(payUp.toUserId, 'someone')}`, {
		date: payUp.payUpDate,
		amount: payUp.amount,
		currencyCode: payUp.currencyCode,
	})
}

export interface TagSourceData {
	members: readonly Member[]
	flightGroups: readonly GetFlightGroupResponse[]
	accommodations: readonly GetAccommodationResponse[]
	events: readonly GetEventResponse[]
	ideas: readonly GetIdeaResponse[]
	expenses: readonly GetExpenseResponse[]
	payUps: readonly GetPayUpType[]
}

/** Everything taggable, me left out of the people: activities in itinerary order, money newest first. */
export const pickerItems = (data: TagSourceData, myUserId: number): ChatTagPickerItem[] => {
	const item = (card: ChatCard, byline: string | null = null): ChatTagPickerItem => ({ card, byline })
	const firstName = (id: number) => data.members.find(m => m.id === id)?.firstName ?? null
	return [
		...data.members.filter(m => m.id !== myUserId).map(m => item(memberCard(authorOf(m)))),
		...data.flightGroups.map(group => item(flightGroupCard(group, data.members))),
		...data.accommodations.map(accommodation => item(accommodationCard(accommodation, data.members))),
		...[...data.events]
			.sort((a, b) => a.tripDayIndex - b.tripDayIndex || a.eventIndex - b.eventIndex)
			.map(event => item(eventCard(event))),
		...data.ideas.map(idea => item(ideaCard(idea), firstName(idea.createdByUserId))),
		...[...data.expenses].sort((a, b) => b.expenseDate - a.expenseDate).map(expense => item(expenseCard(expense))),
		...[...data.payUps].sort((a, b) => b.payUpDate - a.payUpDate).map(payUp => item(payUpCard(payUp, data.members))),
	]
}
